import axios from 'axios'

/**
 * Every endpoint (per-location and agency) authenticates the same way: the
 * encrypted GHL SSO blob (or, in local dev only, a raw locationId/companyId
 * query param) is sent as part of the request body and resolved server-side
 * — see AdoptionTrackerController::resolveLocationId()/resolveCompanyId().
 * There is no login step and no token to persist: every call re-proves
 * identity, so this service's only job beyond wrapping axios is running the
 * postMessage handshake once per page load and caching its result.
 */
class AdoptionTrackerAPI {
  constructor() {
    this.baseURL = '/api/adoption'
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    })

    this._cachedEncrypted = null
    this._devLocationId = new URLSearchParams(window.location.search).get('locationId')
    this._devCompanyId = new URLSearchParams(window.location.search).get('companyId')
  }

  /**
   * The agency dashboard / report routes carry {companyId?} as a path segment
   * (not a query param), so Blade renders it server-side and the mount tag
   * passes it in as a prop — this lets that value override the query-string
   * check above. Ignored when falsy so the query-string bypass still works.
   */
  setDevCompanyId(companyId) {
    if (companyId) this._devCompanyId = companyId
  }

  /**
   * Pre-seeds the SSO blob cache with a server-generated encrypted payload —
   * used by the signed "direct link" dashboard route (AdoptionTrackerController::
   * showDashboardDirect()), which builds a real SSO-shaped blob server-side
   * instead of the browser ever doing the postMessage handshake. Every API call
   * still goes through the exact same decrypt path as a genuine GHL session.
   */
  setDirectEncrypted(encrypted) {
    if (encrypted) this._cachedEncrypted = encrypted
  }

  /**
   * Resolves the GHL postMessage handshake (REQUEST_USER_DATA /
   * REQUEST_USER_DATA_RESPONSE). Resent every 2s for up to 20s, since the
   * parent GHL frame isn't always listening the instant this iframe's script
   * runs. Cached after the first successful resolution — every subsequent
   * call in the same page load reuses the same encrypted blob.
   */
  getEncryptedUserData() {
    if (this._cachedEncrypted) {
      return Promise.resolve(this._cachedEncrypted)
    }

    return new Promise((resolve, reject) => {
      const giveUp = setTimeout(() => {
        clearInterval(resend)
        window.removeEventListener('message', handler)
        reject(new Error('Timed out waiting for GHL session data.'))
      }, 20000)

      const ask = () => {
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*')
        if (window.top !== window.parent) {
          window.top.postMessage({ message: 'REQUEST_USER_DATA' }, '*')
        }
      }
      ask()
      const resend = setInterval(ask, 2000)

      const handler = ({ data }) => {
        console.debug('AdoptionTrackerAPI: got postMessage', data)
        if (data && data.message === 'REQUEST_USER_DATA_RESPONSE') {
          clearTimeout(giveUp)
          clearInterval(resend)
          window.removeEventListener('message', handler)
          this._cachedEncrypted = data.payload
          resolve(data.payload)
        }
      }
      window.addEventListener('message', handler)
    })
  }

  /** Auth payload for a per-location endpoint (dev bypass uses ?locationId=). */
  async locationAuthPayload() {
    if (this._devLocationId) return { locationId: this._devLocationId }
    return { encrypted: await this.getEncryptedUserData() }
  }

  /** Auth payload for an agency endpoint (dev bypass uses ?companyId=). */
  async agencyAuthPayload() {
    if (this._devCompanyId) return { companyId: this._devCompanyId }
    return { encrypted: await this.getEncryptedUserData() }
  }

  async post(path, auth, extra = {}) {
    const response = await this.client.post(path, { ...auth, ...extra })
    return response.data
  }

  async patch(path, auth, extra = {}) {
    const response = await this.client.patch(path, { ...auth, ...extra })
    return response.data
  }

  // ==========================================
  // Per-location dashboard
  // ==========================================

  async getDashboardData() {
    return this.post('/data', await this.locationAuthPayload())
  }

  async getHistory(days = 90) {
    return this.post('/history', await this.locationAuthPayload(), { days })
  }

  async getEvents(days = 14, metric = null) {
    const extra = { days }
    if (metric) extra.metric = metric
    return this.post('/events', await this.locationAuthPayload(), extra)
  }

  async triggerScan() {
    return this.post('/scan', await this.locationAuthPayload())
  }

  async updateWindow(windowDays) {
    return this.patch('/window', await this.locationAuthPayload(), { window_days: windowDays })
  }

  async getWindowBackfillStatus() {
    return this.post('/window/status', await this.locationAuthPayload())
  }

  async getThresholds() {
    return this.post('/thresholds', await this.locationAuthPayload())
  }

  async updateThresholds(thresholds) {
    return this.patch('/thresholds', await this.locationAuthPayload(), { thresholds })
  }

  // ==========================================
  // Agency dashboard
  // ==========================================

  async getAgencyData() {
    return this.post('/agency/data', await this.agencyAuthPayload())
  }

  async getMetricLocationReport(page = 1, search = '', perPage = 10) {
    const extra = { page, per_page: perPage }
    if (search) extra.search = search
    return this.post('/agency/metric-location-report', await this.agencyAuthPayload(), extra)
  }

  async getAgencyWindow() {
    return this.post('/agency/window', await this.agencyAuthPayload())
  }

  async updateAgencyWindow(windowDays) {
    return this.patch('/agency/window', await this.agencyAuthPayload(), { window_days: windowDays })
  }
}

export default new AdoptionTrackerAPI()
